import { useState, useEffect } from "react";
import {
  Dropdown,
  Option,
  makeStyles,
  shorthands,
} from "@fluentui/react-components";
import moment from "moment";
import DynamicDatePicker from "../ControlledDatePicker/dynamicDatepicker";
import { resolveDateRangePreset } from "../../shared/resolveDateRangePreset";
import { DateRangePreset } from "../../types/reportTypes";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: "220px",
    flexShrink: 0,
  },
  dropdown: {
    minWidth: "220px",
  },
  datePickers: {
    display: "flex",
    flexWrap: "wrap",
    ...shorthands.gap("8px"),
    marginTop: "8px",
  },
  dateField: {
    flex: "1 1 180px",
    minWidth: "180px",
  },
});

export type { DateRangePreset };

export interface DateRangeValue {
  preset: string;
  startDate: string | null;
  endDate: string | null;
}

interface DateRangePickerProps {
  presets: DateRangePreset[];
  value: DateRangeValue | null;
  onChange: (value: DateRangeValue) => void;
  placeholder?: string;
  format?: string;
  disabled?: boolean;
}

// Resolves a preset (or explicit custom start/end) into concrete dates.
// The actual per-preset date math lives in resolveDateRangePreset so it can
// be shared with other date-range pickers (e.g. CommissionReceipts/FilterTab.tsx).
export const resolveDateRange = (
  preset: string,
  customStart: Date | null,
  customEnd: Date | null,
  format = "YYYY-MM-DD"
): { startDate: string | null; endDate: string | null } => {
  const resolved = resolveDateRangePreset(preset, customStart, customEnd);

  if (!resolved) {
    if (preset) {
      console.warn(`DateRangePicker: unrecognized preset "${preset}" — no date range resolved.`);
    }
    return { startDate: null, endDate: null };
  }

  return {
    startDate: resolved.start ? resolved.start.format(format) : null,
    endDate: resolved.end ? resolved.end.format(format) : null,
  };
};

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  presets,
  value,
  onChange,
  placeholder = "Select date range",
  format = "YYYY-MM-DD",
  disabled = false,
}) => {
  const styles = useStyles();
  const selectedPreset = value?.preset || "";

  const [customStart, setCustomStart] = useState<Date | null>(
    value?.startDate ? moment(value.startDate, format).toDate() : null
  );
  const [customEnd, setCustomEnd] = useState<Date | null>(
    value?.endDate ? moment(value.endDate, format).toDate() : null
  );

  useEffect(() => {
    if (selectedPreset !== "custom") return;
    setCustomStart(
      value?.startDate ? moment(value.startDate, format).toDate() : null
    );
    setCustomEnd(
      value?.endDate ? moment(value.endDate, format).toDate() : null
    );
    // Only re-sync from the external value while in custom mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.startDate, value?.endDate, selectedPreset]);

  const handlePresetSelect = (preset: string) => {
    if (preset === "custom") {
      onChange({
        preset,
        startDate: customStart ? moment(customStart).format(format) : null,
        endDate: customEnd ? moment(customEnd).format(format) : null,
      });
      return;
    }
    onChange({ preset, ...resolveDateRange(preset, null, null, format) });
  };

  // DynamicDatePicker reports changes through formik.setFieldValue, always
  // passing a "YYYY-MM-DD" string (see its handleDateChange), not a Date.
  // Parsing that with moment's explicit-format parser (local time) instead
  // of `new Date(string)` (UTC) avoids an off-by-one-day shift for users in
  // timezones behind UTC on the next render.
  const handleCustomDateChange = (
    which: "start" | "end",
    rawValue: Date | string | null
  ) => {
    const date =
      typeof rawValue === "string"
        ? moment(rawValue, "YYYY-MM-DD").toDate()
        : rawValue;
    const nextStart = which === "start" ? date : customStart;
    const nextEnd = which === "end" ? date : customEnd;

    if (which === "start") setCustomStart(date);
    else setCustomEnd(date);

    onChange({
      preset: "custom",
      startDate: nextStart ? moment(nextStart).format(format) : null,
      endDate: nextEnd ? moment(nextEnd).format(format) : null,
    });
  };

  return (
    <div className={styles.container}>
      <Dropdown
        className={styles.dropdown}
        placeholder={
          selectedPreset
            ? presets.find((p) => p.value === selectedPreset)?.label ??
              placeholder
            : placeholder
        }
        selectedOptions={selectedPreset ? [selectedPreset] : []}
        onOptionSelect={(_, data) => handlePresetSelect(data.optionValue as string)}
        disabled={disabled}
      >
        {presets.map((preset) => (
          <Option key={preset.value} value={preset.value}>
            {preset.label}
          </Option>
        ))}
      </Dropdown>

      {selectedPreset === "custom" && (
        <div className={styles.datePickers}>
          <div className={styles.dateField}>
            <DynamicDatePicker
              border
              name="start_date"
              label="Start Date"
              required={false}
              disabled={disabled}
              formik={{
                values: { start_date: customStart },
                setFieldValue: (_name: string, date: Date | string | null) =>
                  handleCustomDateChange("start", date),
                setFieldTouched: () => {},
                setFieldError: () => {},
                errors: {},
                touched: {},
              }}
            />
          </div>

          <div className={styles.dateField}>
            <DynamicDatePicker
              border
              name="end_date"
              label="End Date"
              required={false}
              disabled={disabled}
              formik={{
                values: { end_date: customEnd },
                setFieldValue: (_name: string, date: Date | string | null) =>
                  handleCustomDateChange("end", date),
                setFieldTouched: () => {},
                setFieldError: () => {},
                errors: {},
                touched: {},
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
